import type { OperationResult } from "@/lib/api/assistant";
import { PriceAverageResult } from "./PriceAverageResult";
import { SignalWindowMetricResult } from "./SignalWindowMetricResult";
import { SignalLookupResult } from "./SignalLookupResult";
import { PriceLookupResult } from "./PriceLookupResult";

/**
 * Render a single OperationResult by dispatching on `kind`.
 *
 * The discriminator (`kind`) MUST cover every union member; when a new kind
 * is added to AssistantResponse, this switch falls through to the
 * "unsupported result kind" panel so the UI never silently drops data.
 */
export function ResultRenderer({ result }: { result: OperationResult }) {
  switch (result.kind) {
    case "price_average":
      return <PriceAverageResult rows={result.rows} />;
    case "signal_window_metric":
      return <SignalWindowMetricResult rows={result.rows} />;
    case "signal_lookup":
      return <SignalLookupResult rows={result.rows} />;
    case "price_lookup":
      return <PriceLookupResult rows={result.rows} />;
    default: {
      // Exhaustiveness guard. If a future result kind is added without UI,
      // we surface that to the user instead of silently dropping the rows.
      const r: { kind: string } = result;
      return (
        <div className="border border-slate-300 bg-slate-50 rounded-lg p-4">
          <div className="text-sm font-semibold text-slate-800">
            Unsupported result kind: <span className="font-mono">{r.kind}</span>
          </div>
          <div className="text-xs text-slate-600 mt-1">
            The assistant returned a result type the admin portal does not yet
            know how to render. Raw payload below.
          </div>
          <pre className="mt-2 text-[11px] bg-white border border-slate-200 rounded p-2 overflow-x-auto">
            {JSON.stringify(result, null, 2)}
          </pre>
        </div>
      );
    }
  }
}

export function ResultsRenderer({ results }: { results: OperationResult[] }) {
  if (results.length === 0) return null;
  return (
    <div className="space-y-3">
      {results.map((r, i) => (
        <ResultRenderer key={i} result={r} />
      ))}
    </div>
  );
}
